/**
 * useDocAI — the single client wrapper for the DocAI platform callables
 * (hidden /ai section, SuperAdmin only).
 *
 *   callDocai('docaiDocuments', { action: 'list', status: 'review' })
 *
 * All six callables are action-style (manageCrmDocuments precedent) and run
 * in us-east1. Long jobs return a runId; useRunPoller polls docaiRuns.get
 * until the run reaches a terminal status, exposing live progress
 * ({ done, total, phase }) along the way.
 *
 * sha256HexOfFile hashes upload bytes client-side (crypto.subtle) because
 * PDFs are stored content-addressed as <sha256>.pdf and the uploadUrl action
 * dedupes on the hash before any bytes move.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functionsUsEast1 } from '../utils/init-firebase';

const CALLABLES = [
  'docaiDocuments',
  'docaiReview',
  'docaiSkills',
  'docaiTaxonomy',
  'docaiRuns',
  'docaiStats',
];

/** Call one DocAI callable. Throws Error(message) on failure. */
export async function callDocai(fnName, payload) {
  if (!CALLABLES.includes(fnName)) throw new Error(`Unknown DocAI callable: ${fnName}`);
  const fn = httpsCallable(functionsUsEast1, fnName);
  try {
    const res = await fn(payload || {});
    return res.data;
  } catch (err) {
    // firebase HttpsError → readable message
    throw new Error(err?.message || 'DocAI call failed');
  }
}

/** SHA-256 hex of a File/Blob via WebCrypto (for content-addressed uploads). */
export async function sha256HexOfFile(file) {
  const bytes = await file.arrayBuffer();
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

const TERMINAL = ['done', 'error', 'cancelled'];

/**
 * Poll a run until terminal. Returns { run, isPolling, refresh }.
 * Pass null runId to idle.
 */
export function useRunPoller(runId, { intervalMs = 2500 } = {}) {
  const [run, setRun] = useState(null);
  const [isPolling, setIsPolling] = useState(false);
  const timerRef = useRef(null);
  const runIdRef = useRef(runId);
  runIdRef.current = runId;

  const refresh = useCallback(async () => {
    if (!runIdRef.current) return null;
    const data = await callDocai('docaiRuns', { action: 'get', runId: runIdRef.current });
    setRun(data.run);
    return data.run;
  }, []);

  useEffect(() => {
    if (!runId) {
      setRun(null);
      setIsPolling(false);
      return undefined;
    }
    let alive = true;
    setIsPolling(true);

    const tick = async () => {
      try {
        const current = await refresh();
        if (!alive) return;
        if (current && TERMINAL.includes(current.status)) {
          setIsPolling(false);
          return; // stop polling
        }
      } catch (err) {
        // Transient poll errors: keep trying; surface nothing.
      }
      if (alive) timerRef.current = setTimeout(tick, intervalMs);
    };
    tick();

    return () => {
      alive = false;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [runId, intervalMs, refresh]);

  return { run, isPolling, refresh };
}

/** Fire-and-track: start a run via any callable action that returns { runId }. */
export function useRunLauncher() {
  const [runId, setRunId] = useState(null);
  const poller = useRunPoller(runId);
  const launch = useCallback(async (fnName, payload) => {
    const data = await callDocai(fnName, payload);
    if (data?.runId) setRunId(data.runId);
    return data;
  }, []);
  const reset = useCallback(() => setRunId(null), []);
  return { ...poller, runId, launch, reset };
}

export default callDocai;
